/**
 * What a headless run does with the page's console.
 *
 * The runner only sees a flat console stream. The runtime announces each step with a
 * {@link StepStartMark} line, which is ordered with everything the step then logs by construction —
 * so attribution needs no round-trip into the page. Errors and warnings are always forwarded (a
 * game guard explaining a refused payload is exactly the diagnostic a red step needs); plain
 * `console.log` is forwarded only when the run is verbose. `ctx.log` lines and uncaught page errors
 * are always forwarded and attributed, the same as errors and warnings. Whatever was forwarded
 * during a step is kept, bounded, and attached to that step's errors in the summary.
 */
import { PLAYGROUND_STEP_LOG_PREFIX, PLAYGROUND_TEST_LOG_PREFIX, parseStepStart, type StepStartMark } from './protocol.js';
import type { PlaygroundTestRunSummary, PlaygroundTestSummaryStep } from './run-tests.js';

export type ForwardLevel = 'error' | 'warn' | 'info';

export interface ForwardedLine {
  level: ForwardLevel;
  /** The text to log, tagged with the running step's label when one is known. */
  line: string;
}

export interface ConsoleCaptureOptions {
  /** Forward `console.log`-class messages too. Errors and warnings are forwarded regardless. */
  verbose: boolean;
  /** How many lines to keep per step. Default 50. */
  keep?: number;
}

const DEFAULT_KEEP = 50;

/** Playwright's `ConsoleMessage.type()` values that a run always surfaces, and the level they map to. */
const ALWAYS_FORWARDED: Readonly<Record<string, ForwardLevel>> = {
  error: 'error',
  assert: 'error',
  warning: 'warn',
};

/** Unambiguous for any test id or index — and, unlike a control character, keeps this file text. */
const stepKey = (testId: string, stepIndex: number) => JSON.stringify([testId, stepIndex]);

export class ConsoleCapture {
  readonly #keep: number;
  readonly #verbose: boolean;
  readonly #lines = new Map<string, string[]>();
  #current: StepStartMark | null = null;

  constructor(options: ConsoleCaptureOptions) {
    this.#verbose = options.verbose;
    this.#keep = options.keep ?? DEFAULT_KEEP;
  }

  /**
   * Feed one console message (Playwright's `type()` and `text()`). Returns what to forward to the
   * run's logger, or `null` when the message is dropped.
   */
  observe(type: string, text: string): ForwardedLine | null {
    if (text.startsWith(PLAYGROUND_TEST_LOG_PREFIX)) {
      const mark = parseStepStart(text);
      if (mark) {
        this.#current = mark;
        // A step starting again means the page booted again — a reload before READY, the runner's own (a re-bundle,
        // `reloadBeforeReady`) or Vite's with HMR on — and its suite runs anew: a step's lines are those of its latest
        // run. A document reloaded before READY never publishes the run's result (the runner ignores it), so the latest
        // run is the one whose result the runner takes.
        this.#lines.delete(stepKey(mark.testId, mark.stepIndex));
      }
      // The in-page runner's own reporting. Keep its severity — it reports suite failures too —
      // but never attribute it to a step: it is the runner talking, not the game.
      return { level: ALWAYS_FORWARDED[type] ?? 'info', line: text };
    }

    // A step's own report line (`ctx.log`): the game talking about the step, so it is always
    // forwarded, tagged, and kept on the step — unlike the runner's prefix above.
    if (text.startsWith(PLAYGROUND_STEP_LOG_PREFIX)) {
      const body = text.slice(PLAYGROUND_STEP_LOG_PREFIX.length).trimStart();
      if (this.#current) {
        this.#record(this.#current, body);
        return { level: 'info', line: `[${this.#current.label}] ${body}` };
      }
      return { level: 'info', line: body };
    }

    const level = ALWAYS_FORWARDED[type] ?? (this.#verbose ? 'info' : null);
    if (level === null) {
      return null;
    }
    if (this.#current) {
      this.#record(this.#current, text);
      return { level, line: `[${this.#current.label}] ${text}` };
    }
    return { level, line: text };
  }

  /** The forwarded lines a step produced, oldest first. */
  linesFor(testId: string, stepIndex: number): readonly string[] {
    return this.#lines.get(stepKey(testId, stepIndex)) ?? [];
  }

  /** An uncaught exception the page raised (Playwright's `pageerror`): forwarded as an error, tagged with the running step. */
  observePageError(message: string): ForwardedLine {
    const text = `Uncaught ${message}`;
    if (this.#current) {
      this.#record(this.#current, text);
      return { level: 'error', line: `[${this.#current.label}] ${text}` };
    }
    return { level: 'error', line: text };
  }

  #record(mark: StepStartMark, text: string): void {
    const key = stepKey(mark.testId, mark.stepIndex);
    const lines = this.#lines.get(key) ?? [];
    lines.push(text);
    if (lines.length > this.#keep) {
      lines.splice(0, lines.length - this.#keep);
    }
    this.#lines.set(key, lines);
  }
}

const withConsole = (step: PlaygroundTestSummaryStep, lines: readonly string[]): PlaygroundTestSummaryStep =>
  step.errors.length === 0 || lines.length === 0
    ? step
    : { ...step, errors: step.errors.map((error) => ({ ...error, console: [...lines] })) };

/** A copy of the summary in which every failed step's errors carry the console lines that step produced. */
export const attachConsoleToSummary = (summary: PlaygroundTestRunSummary, capture: ConsoleCapture): PlaygroundTestRunSummary => ({
  ...summary,
  tests: summary.tests.map((test) => ({
    ...test,
    steps: test.steps.map((step, index) => withConsole(step, capture.linesFor(test.id, index))),
  })),
});
